/**
 * +polish: open-field camera occlusion. Props (merged per chunk, so no per-object fade) dither
 * away inside a capsule around the sight line from the camera to the hero, the outdoor cousin of
 * the dungeon cutaway (../cutaway.ts) and the hero self-fade (../selfFade.ts). Screen-door
 * discard: opaque pipeline, no sorting; a sparse remainder keeps the silhouette readable.
 */
import * as THREE from 'three';

export const OCC_U = {
  uOccCam: { value: new THREE.Vector3(0, 1e4, 0) },
  uOccFocus: { value: new THREE.Vector3() },
  /** x: capsule radius at the hero (yd), y: widening per yd toward the camera, z: on (0/1), w: max cut (0..1). */
  uOccP: { value: new THREE.Vector4(1.4, 0.1, 0, 0.82) },
};

/** Aim the occlusion capsule (three.js space; `focus` = the hero's chest). */
export function updateFieldOcclusion(cam: THREE.Vector3, focusX: number, focusY: number, focusZ: number): void {
  OCC_U.uOccCam.value.copy(cam);
  OCC_U.uOccFocus.value.set(focusX, focusY, focusZ);
  OCC_U.uOccP.value.z = 1;
}

/**
 * How much of a point is cut (0..1, before dithering): the CPU twin of the shader, for tests.
 * `p`, `cam`, `focus` in three.js space.
 */
export function occlusionAmount(p: THREE.Vector3, cam: THREE.Vector3, focus: THREE.Vector3, r0 = 1.4, slope = 0.1, max = 0.82): number {
  const seg = cam.clone().sub(focus);
  const L = seg.length();
  if (L < 0.01) return 0;
  const u = seg.divideScalar(L);
  const rel = p.clone().sub(focus);
  const along = rel.dot(u);
  const perp = rel.clone().addScaledVector(u, -along).length();
  const r = r0 + slope * Math.max(along, 0);
  const ss = (e0: number, e1: number, x: number): number => {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
  };
  return (1 - ss(r - 0.6, r, perp)) * ss(0.8, 1.8, along) * (1 - ss(L - 0.5, L, along)) * max;
}

const VERT_DECL = `varying vec3 vOccW;`;
const VERT_BODY = `
  {
    vec4 ow = vec4(transformed, 1.0);
    #ifdef USE_INSTANCING
      ow = instanceMatrix * ow;
    #endif
    vOccW = (modelMatrix * ow).xyz;
  }`;
const FRAG_DECL = `
  varying vec3 vOccW;
  uniform vec3 uOccCam;
  uniform vec3 uOccFocus;
  uniform vec4 uOccP;
  float occBayer2(vec2 a) { a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }
  float occBayer4(vec2 a) { return occBayer2(0.5 * a) * 0.25 + occBayer2(a); }`;
const FRAG_BODY = `
  if (uOccP.z > 0.5) {
    vec3 seg = uOccCam - uOccFocus;
    float L = length(seg);
    vec3 u = seg / max(L, 0.01);
    vec3 rel = vOccW - uOccFocus;
    float along = dot(rel, u);
    float perp = length(rel - u * along);
    float r = uOccP.x + uOccP.y * max(along, 0.0);
    float k = (1.0 - smoothstep(r - 0.6, r, perp)) * smoothstep(0.8, 1.8, along) * (1.0 - smoothstep(L - 0.5, L, along)) * uOccP.w;
    // +zone: deep in the capsule a prop is simply gone (no dither left to read as noise), and
    // anything right at the lens is cut (big conifers the camera brushes against).
    float near = length(vOccW - uOccCam);
    if (near < 5.0 || k > 0.6) discard;
    if (near < 7.0 && smoothstep(7.0, 5.0, near) > occBayer4(gl_FragCoord.xy) + 0.01) discard;
    #ifdef OCC_HULL
      // The outline hull (purple-dark back faces) never shows through a fading prop, nor from
      // inside a big conifer the camera stands in.
      if (k > 0.04 || near < 14.0) discard;
    #endif
    if (k > 0.002 && k > occBayer4(gl_FragCoord.xy) + 0.01) discard;
  }`;

/** Patch a material (toon or the outline hull's basic) to obey the occlusion capsule. Idempotent. */
export function occludable<M extends THREE.Material>(m: M): M {
  if (m.userData['occ']) return m;
  m.userData['occ'] = true;
  const base = m.onBeforeCompile;
  m.onBeforeCompile = (sh, r) => {
    base.call(m, sh, r);
    Object.assign(sh.uniforms, OCC_U);
    sh.vertexShader = sh.vertexShader.replace('#include <common>', `#include <common>\n${VERT_DECL}`).replace('#include <project_vertex>', `#include <project_vertex>\n${VERT_BODY}`);
    sh.fragmentShader = sh.fragmentShader.replace('#include <common>', `#include <common>\n${m.side === THREE.BackSide ? '#define OCC_HULL\n' : ''}${FRAG_DECL}`).replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>\n${FRAG_BODY}`);
  };
  const key = m.customProgramCacheKey.bind(m);
  m.customProgramCacheKey = () => `${key()}|occ`;
  return m;
}
